import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import {
  Combobox,
  type ComboboxFieldProps,
  type ComboboxOption,
} from "@/common/components/ui/combobox";
import { Skeleton } from "@/common/components/ui/skeleton";
import {
  GetLedgerCurrenciesDocument,
  type GetLedgerCurrenciesQuery,
  type GetLedgerCurrenciesQueryVariables,
} from "@/graphql/definitions";
import { useTranslations } from "@/common/hooks/use-translations";
import { useSuggestionsFallback } from "./use-suggestions-fallback";

interface CurrencyComboboxProps extends ComboboxFieldProps {
  ledgerId: string;
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * CurrencyCombobox component - searchable combobox for currency selection
 * Uses GetLedgerCurrencies GraphQL query to fetch available currencies
 */
export function CurrencyCombobox({
  ledgerId,
  value,
  onValueChange,
  placeholder,
  disabled = false,
  className,
  ...fieldProps
}: CurrencyComboboxProps) {
  const { t } = useTranslations();
  const defaultPlaceholder = placeholder || t("journal.selectCurrency");

  const query = useQuery<
    GetLedgerCurrenciesQuery,
    GetLedgerCurrenciesQueryVariables
  >(GetLedgerCurrenciesDocument, {
    variables: { ledgerId },
    skip: !ledgerId,
    notifyOnNetworkStatusChange: true,
  });
  const { data } = query;

  // Convert currencies to ComboboxOption format
  const options: ComboboxOption[] = useMemo(() => {
    if (!data?.getLedgerCurrencies) return [];

    return data.getLedgerCurrencies.map((currency) => ({
      value: currency,
      label: currency,
    }));
  }, [data]);

  const fallback = useSuggestionsFallback(
    query,
    Boolean(data?.getLedgerCurrencies),
    fieldProps["aria-describedby"],
  );

  if (fallback.initialLoading) {
    return <Skeleton className="h-10 w-full" />;
  }

  return (
    <Combobox
      options={fallback.failed ? [] : options}
      value={value}
      onValueChange={onValueChange}
      placeholder={defaultPlaceholder}
      allowCustom={true}
      emptyText={t("journal.noCurrenciesFound")}
      disabled={disabled}
      className={className}
      {...fieldProps}
      aria-describedby={fallback.describedBy}
      notice={fallback.notice}
    />
  );
}
